import { IS_SSR, React, Settings } from '@/config'
import { useMemo } from '@codeleap/hooks'
import { Button, View, Text, Tooltip, Icon } from '@/components'
import { shareOptions } from '@/utils'
import { createStyles } from '@codeleap/styles'
import { useLingui } from '@lingui/react/macro'
import { modal } from '@codeleap/modals'

export const ShareModal = modal<{ data: ShareData }>({ id: 'share' })
  .content(props => {
    const { data: shareData, toggle } = props

    const { t } = useLingui()

    const content = {
      url: shareData?.url,
      title: shareData?.text,
      description: shareData?.text,
      text: shareData?.text,
      summary: shareData?.text,
      quote: shareData?.title,
      appId: Settings.ApiCredentials.FacebookSDK.AppId,
      separator: ' ',
    }

    const canClipboard = useMemo(() => {
      if (IS_SSR) return

      return !!navigator.clipboard
    }, [])

    const copyLink = React.useCallback(async () => {
      if (!canClipboard) return

      await navigator.clipboard.writeText(content?.url)
    }, [content?.url, canClipboard])

    const options = React.useMemo(() => {
      return Object.keys(shareOptions).map(provider => {
        const option = shareOptions?.[provider]
        const { Action, color } = option ?? {}

        const ProviderIcon = option?.Icon ?? Icon

        const url = option?.parseUrl?.(content?.url) ?? content?.url

        return (
          <Action
            key={provider + 'share-modal'}
            {...content}
            url={url}
            style={{ ...styles.share, border: `1px solid ${color}` }}
          >
            <View style={['flex', 'fullWidth', 'fullHeight', 'center']}>
              <ProviderIcon name={provider} size={ICON_SIZE} />
            </View>
          </Action>
        )
      })
    }, [content])

    return (
      <>
        <View
          style={[
            'column',
            'fullWidth',
            'alignStart',
            'justifyCenter',
            'marginBottom:1',
          ]}
        >
          <Text
            style={['h2', 'ellipsis', styles.text]}
            text={shareData?.title ?? t`Share`}
          />
        </View>

        <View
          style={[
            'column',
            'fullWidth',
            'alignStart',
            'justifyCenter',
            'gap:3',
          ]}
        >
          <Text style={['p1']} text={shareData?.text ?? t`Share the content`} />

          <View style={['row', 'wrap', 'gap:1', 'center']}>{options}</View>

          <View style={['fullWidth', 'row', 'gap:2']}>
            <Tooltip
              debugName={'shareModal:tooltip'}
              content={
                <Text
                  text={t`Copied!`}
                  style={['p2', 'color:neutralSolid1000']}
                />
              }
              openOnPress
              openOnHover={false}
              side="top"
              closeOnClickOutside
              style={['small', 'success', 'flex', 'overlap']}
              disabled={!canClipboard}
            >
              <Button
                debugName="shareModal:clipboard"
                text={t`Copy Link`}
                onPress={copyLink}
                style={[
                  'outline',
                  'fullWidth',
                  'large',
                  'pill',
                  'flex',
                  'px:4',
                ]}
                disabled={!canClipboard}
              />
            </Tooltip>

            <Button
              debugName="shareModal:done"
              text={t`Done`}
              onPress={toggle}
              style={['large', 'pill', 'flex', 'fullWidth']}
            />
          </View>
        </View>
      </>
    )
  })
  .props({ showClose: false, style: ['centered', 'box:responsiveness'] })

const ICON_SIZE = 24

const styles = createStyles(theme => ({
  share: {
    borderRadius: theme.radius.regular,
    overflow: 'hidden',
    ...theme.presets.center,
    ...theme.spacing.paddingHorizontal(4),
    maxHeight: ICON_SIZE * 2,
    height: ICON_SIZE * 2,
  },
  text: {
    maxWidth: '100%',
  },
}))
